import React from "react";
import { PetItems } from "../../utils/Customer/CustomerTypes";
import { ImagePath } from "../../assests";
import { IPetItem } from "../../types/Response/CustomerResponse";

interface PetGridSectionProps {
  pets: (IPetItem | PetItems | any)[];
  handlePetPage?: (pet: any) => void;
}

const PetGridSection: React.FC<PetGridSectionProps> = ({
  pets,
  handlePetPage,
}) => {

  const getPetAge = (birthDate: string | Date | null | undefined): string => {
    if (!birthDate) return "";

    const birth = new Date(birthDate);
    const today = new Date();

    if (isNaN(birth.getTime())) return "";

    // Prevent future dates
    if (birth > today) return "";

    let years = today.getFullYear() - birth.getFullYear();
    let months = today.getMonth() - birth.getMonth();
    let days = today.getDate() - birth.getDate();

    if (days < 0) {
      months--;

      const previousMonth = new Date(
        today.getFullYear(),
        today.getMonth(),
        0
      );

      days += previousMonth.getDate();
    }

    if (months < 0) {
      years--;
      months += 12;
    }

    // Less than 1 month
    if (years === 0 && months === 0) {
      if (days === 0) {
        return "1 day old";
      }

      return `${days} ${days === 1 ? "day" : "days"} old`;
    }

    // Less than 1 year
    if (years === 0) {
      return `${months} ${months === 1 ? "month" : "months"} old`;
    }

    // Years only
    if (months === 0) {
      return `${years} ${years === 1 ? "year" : "years"} old`;
    }

    // Years + months
    return `${years} ${years === 1 ? "year" : "years"} ${months === 1 ? "month" : "months"
      } old`;
  };
  return (
    <section className="relative overflow-hidden">
      <div className="relative z-10">
        <div
          className="
            grid
            grid-cols-2
            sm:grid-cols-2
            md:grid-cols-3
            lg:grid-cols-4
            gap-3
            sm:gap-4
            pt-2
            items-stretch
          "
        >
          {pets.map((pet, index) => {
            const petId =
              pet.petId || pet.PetId || pet.id || index;

            const petName =
              pet.petName || pet.PetName || "Companion";

            const petImage =
              pet.productImage ||
              pet.ProductImage ||
              pet.image ||
              pet.thumbnailUrl ||
              pet.ThumbnailUrl ||
              ImagePath.NoImage;

            const breed =
              pet.breed || pet.Breed || "";

            const gender =
              pet.gender || pet.Gender || "";

            const birthDate = pet.age || pet.Age || "";

            const age = getPetAge(birthDate);

            // Price normalization
            const priceValue =
              pet.salePrice ??
              pet.SalePrice ??
              pet.price ??
              pet.Price;

            const rawPrice = Number(priceValue);

            const hasPrice =
              priceValue !== null &&
              priceValue !== undefined &&
              priceValue !== "" &&
              !isNaN(rawPrice) &&
              rawPrice > 0;

            // Health & Status indicators
            const isVaccinated =
              pet.isVaccinated ??
              pet.IsVaccinated ??
              pet.vaccinated ??
              false;

            const isPureBreed =
              pet.isKCIRegistered ??
              pet.IsKCIRegistered ??
              pet.pureBreed ??
              false;

            const isAvailable =
              pet.available ??
              (
                pet.petStatus === "Available" ||
                pet.PetStatus === 1 ||
                pet.petStatus === 1 ||
                !pet.petStatus
              );

            const isAdopt =
              pet.petType === "Adopt";

            return (
              <div
                key={petId}
                onClick={() => handlePetPage?.(pet)}
                className="
                  group
                  w-full
                  rounded-2xl
                  border
                  border-gray-200
                  bg-white
                  cursor-pointer
                  overflow-hidden
                  flex
                  flex-col
                  h-full
                  transition-all
                  duration-200
                  hover:shadow-md
                "
              >
                {/* ================= IMAGE ================= */}
                <div
                  className={`
                    relative
                    w-full
                    bg-white
                    flex
                    items-center
                    justify-center
                    overflow-hidden
                    ${!hasPrice || isAdopt
                      ? "h-[250px] sm:h-[270px] md:h-[280px] lg:h-[290px]"
                      : "h-[200px] sm:h-[220px] md:h-[230px] lg:h-[240px]"
                    }
                  `}
                >
                  <img
                    src={petImage}
                    alt={petName}
                    onError={(e) => {
                      e.currentTarget.src = ImagePath.NoImage;
                    }}
                    className="
                      h-full
                      w-full
                      object-cover
                      transition-transform
                      duration-500
                      group-hover:scale-105
                    "
                  />

                  {/* Gender Badge */}
                  {gender && (
                    <div className="absolute top-2 right-2 sm:top-3 sm:right-3">
                      <span
                        className="
                          rounded-full
                          bg-white/90
                          backdrop-blur
                          px-2
                          sm:px-3
                          py-1
                          text-[10px]
                          sm:text-xs
                          font-semibold
                          text-logoHeading
                          shadow
                          capitalize
                        "
                      >
                        {gender}
                      </span>
                    </div>
                  )}
                </div>

                {/* ================= DETAILS ================= */}
                <div className="flex flex-col justify-between p-2.5 sm:p-3 flex-1">
                  <div>
                    {/* Pet Name */}
                    <h3
                      className="
                        text-sm
                        sm:text-base
                        font-semibold
                        text-logoHeading
                        line-clamp-1
                      "
                    >
                      {petName}
                    </h3>

                    {/* Breed */}
                    {breed && (
                      <p
                        className="
                          mt-1
                          text-xs
                          sm:text-sm
                          text-gray-500
                          line-clamp-1
                        "
                      >
                        {breed}
                      </p>
                    )}

                    {/* ================= BADGES ================= */}
                    <div className="mt-2 flex items-center gap-1.5 min-w-0 overflow-hidden">
                      {isVaccinated && (
                        <span className="shrink-0 rounded-full bg-green-100 px-2 py-0.5 text-[10px] sm:text-xs font-medium text-green-700">
                          Vaccinated
                        </span>
                      )}

                      {isPureBreed && (
                        <span className="shrink-0 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] sm:text-xs font-medium text-blue-700">
                          Pure Breed
                        </span>
                      )}

                      {isAvailable && (
                        <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] sm:text-xs font-medium text-amber-700">
                          Available
                        </span>
                      )}


                    </div>
                    {pet.tag && (
                      <div className="mt-1.5 min-w-0 max-w-full">
                        <span
                          title={pet.tag}
                          className="
        inline-block
        max-w-full
        truncate
        rounded-full
        bg-emerald-50
        px-2
        py-0.5
        text-[10px]
        sm:text-xs
        font-medium
        text-emerald-800
        align-middle
      "
                        >
                          {pet.tag}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* ================= BOTTOM ================= */}
                  <div className="mt-auto">
                    {/* Age */}
                    {age && (
                      <div className="mt-2">
                        <div className="flex flex-col">
                          <span
                            className="
                              text-[10px]
                              sm:text-xs
                              text-gray-400
                              uppercase
                            "
                          >
                            Age
                          </span>

                          <span
                            className="
                              text-xs
                              sm:text-sm
                              font-medium
                              text-gray-700
                            "
                          >
                            {age}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* ================= PRICE ================= */}
                    {hasPrice && !isAdopt && (
                      <div className="mt-3">
                        <p
                          className="
                            text-[10px]
                            sm:text-xs
                            text-gray-400
                            uppercase
                          "
                        >
                          Price
                        </p>

                        <p
                          className="
                            text-base
                            sm:text-xl
                            font-bold
                            text-logoHeading
                          "
                        >
                          AED {rawPrice.toLocaleString()}
                        </p>
                      </div>
                    )}

                    {/* ================= BUTTON ================= */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePetPage?.(pet);
                      }}
                      className="
                        mt-3
                        sm:mt-4
                        w-full
                        rounded-lg
                        bg-[#2F5E2E]
                        py-2
                        sm:py-2.5
                        text-xs
                        sm:text-sm
                        font-semibold
                        text-white
                        transition-all
                        duration-200
                        hover:bg-[#6DBE45]
                        cursor-pointer
                      "
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PetGridSection;